import cn from "@/v2-ui/utils/utils.cn"
import { DotText } from "@/v2-backoffice-client/components/dot"
import InfoGrid, {
  createInfoGridFields,
  type InfoGridProps,
} from "@/v2-backoffice-client/components/infoGrid"
import {
  contentDecisionOf,
  contentKindOf,
  CONTENT_DECISION_DOT_VARIANT,
  whoHasContent,
  type LicenseContentItem,
} from "@/v2-backoffice-client/license/changes/license.content.utils"

const FIELDS = createInfoGridFields<LicenseContentItem>({
  fields: {
    Decision: {
      label: "Decision",
      description:
        "Free for everyone: every customer sees it, how an item arrives. Package-only: locked to the packages that include it.",
      accessor: (data) => contentDecisionOf(data),
      renderValue({ data }) {
        const decision = contentDecisionOf(data)
        return (
          <DotText variant={CONTENT_DECISION_DOT_VARIANT[decision]}>
            {decision}
          </DotText>
        )
      },
    },
    WhoHasIt: {
      label: "Who has it",
      description:
        "Who can see the item right now, once it is live. A customer that takes only what is listed (Customers > What they get) is the exception either way: it gets the item only through its own packages.",
      accessor: (data) => whoHasContent(data),
    },
    Kind: {
      label: "Kind",
      accessor: (data) => contentKindOf(data),
    },
    Live: {
      label: "Live",
      description:
        "Whether the review queue has published it. Nobody sees an item that is not live, whatever is decided here; the decision waits for it.",
      accessor: (data) => (data.IsEnabled ? "Live" : "Not live"),
      renderValue({ data }) {
        return (
          <DotText variant={data.IsEnabled ? "success-1" : "muted"}>
            {data.IsEnabled ? "Live" : "Not live"}
          </DotText>
        )
      },
    },
    Countries: {
      label: "Countries",
      description:
        "Where the map offers it. An item with no country is hidden everywhere, however it is decided; countries are set on its own sheet.",
      accessor: (data) => data.CountryCodes ?? "None",
      renderValue({ data }) {
        if (!data.CountryCodes) {
          return <span className="text-destructive">None: hidden</span>
        }
        return data.CountryCodes
      },
    },
    Code: {
      label: "Code",
      description:
        "The item's feature code: what the map checks a user for and what a package carries.",
      accessor: "Code",
      valueClassName: "font-mono text-[11px] truncate",
    },
  },
})

export type LicenseContentViewInfoProps = Omit<
  InfoGridProps<LicenseContentItem>,
  "data" | "fields"
> & {
  item: LicenseContentItem | null | undefined
}

function LicenseContentViewInfo(props: LicenseContentViewInfoProps) {
  const { item, className, ...infoGridProps } = props

  return (
    <InfoGrid
      id="license-content-info"
      className={cn("license-content-view-info", className)}
      data={item}
      fields={FIELDS}
      {...infoGridProps}
    />
  )
}

export default LicenseContentViewInfo
